import { Add, CloudDownload } from '@mui/icons-material';
import { Box, ButtonGroup, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState, useMemo, Fragment } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
// Fetch
import {
  getPunchlistDashboard,
  getPunchlistDashboardCsv,
  getPunchlistTodoListPdf
} from '../../../fetch';
// Models
import { IDateRange, IPunchlistDashboard, IResponse } from '../../../models';
// Components
import {
  Button,
  Loader,
  Page,
  PageTitleWithButton,
  Table as TableComponent,
  Pagination,
  SearchSection,
  Link
} from '../../../components';
import { NeighborhoodList } from './NeighborhoodList';
import { TodoDetailModal } from './punchlist-item/TodoDetailModal';
import { PunchlistFilters } from './PunchlistFilters';
import { LoadingButton } from '@mui/lab';
import { downloadFile } from '../../../helpers';

export const PunchlistDashboard = () => {
  const theme = useTheme();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const [neighborhoodId, setNeighborhoodId] = useState<string | number>('');
  const [isNeighborhoodSearch, setIsNeighborhoodSearch] = useState<boolean>(false);
  const [selectedLotNumber, setSelectedLotNumber] = useState<string>('');
  const [vendorId, setVendorId] = useState<string | number>('');
  const [isTodoModalOpen, showTodoModal] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [showAssignedToFilter, setShowAssignedToFilter] = useState<boolean>(true);
  const [selectedAssigneedTo, setSelectedAssigneedTo] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    neighborhoodId?: number | string;
    vendorId?: number | string;
    startDate?: string;
    endDate?: string;
    itemType?: string | number;
    lotNumber?: string;
    assignedTo?: string;
  } | null>({});
  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(50);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange>({
    startDate: null,
    endDate: null,
    key: 'selection',
    inputValue: ''
  });
  const [showArchived, setShowArchived] = useState<boolean>(false);
  // context
  const { enqueueSnackbar } = useSnackbar();

  const {
    isLoading: isLoadingPunchlists,
    data: punchlistData,
    refetch: refetchPunchlistsRecentActivity
  } = useQuery<IResponse<IPunchlistDashboard[]>, Error>(
    ['punchlists-dashboard', page, perPage, filters, searchedValue, showArchived],
    () =>
      getPunchlistDashboard({
        page: page + 1,
        perPage,
        sortDirection: 'DESC',
        searchText: searchedValue,
        showArchived: showArchived,
        ...filters
      }),
    {
      onSuccess: (d: any) => {
        const data: IResponse<IPunchlistDashboard[]> = d;
        setRecordCount(data.totalRecordCount);
        return d;
      },
      onError: (e: any) => {
        enqueueSnackbar(e?.Detail || `Error loading punchlist recent activity.`, {
          variant: 'error'
        });
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const urlFilters: any = {};

  useEffect(() => {
    if (isLoadingPunchlists) {
      return;
    }
    const queryParams = new URLSearchParams(window.location.search);

    queryParams.forEach((value, key) => {
      urlFilters[key] = value;
    });

    if (Object.keys(urlFilters).length !== 0) {
      if (urlFilters.neighborhoodId) {
        setNeighborhoodId(urlFilters.neighborhoodId);
      }
      if (urlFilters.lotNumber) {
        setSelectedLotNumber(urlFilters.lotNumber);
      }
      if (urlFilters.vendorId) {
        setVendorId(urlFilters.vendorId);
      }
      if (urlFilters.startDate) {
        let inputValue = '';
        if (urlFilters.startDate) inputValue += format(new Date(urlFilters.startDate), 'M/d/yyyy');
        if (urlFilters.endDate)
          inputValue += ' - ' + format(new Date(urlFilters.endDate), 'M/d/yyyy');

        setSelectedDateRange({
          startDate: new Date(urlFilters.startDate),
          endDate: new Date(urlFilters.endDate),
          key: 'selection',
          inputValue: `${inputValue}`
        });
      }

      if (urlFilters.searchedValue) {
        setSearchedValue(urlFilters.searchedValue);
        setSearchValue(urlFilters.searchedValue);
      }
      if (urlFilters.page || urlFilters.page === 0) {
        setPage(Number(urlFilters.page - 1));
      }
      if (urlFilters.perPage) {
        setRowsPerPage(Number(urlFilters.perPage));
      }
      if (urlFilters.assignedTo) {
        setSelectedAssigneedTo(urlFilters.assignedTo);
      }
      updateQueryString(urlFilters);
      setNewFilters(urlFilters);
      setFilters(urlFilters);
      setHasAppliedFilters(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPunchlists]);

  // Retain filters
  const history = useHistory();
  const [queryString, setQueryString] = useState('');
  const updateQueryString = useCallback(
    newFilters => {
      const queryParams = new URLSearchParams();

      Object.keys(newFilters).forEach(key => {
        if (newFilters[key]) {
          queryParams.set(key, newFilters[key]);
        }
      });
      const newQueryString = queryParams.toString();
      setQueryString(newQueryString);
      history.push({ search: newQueryString });
    },
    [history]
  );

  const [newFilters, setNewFilters] = useState<any>({});

  const firstRender = useRef(true);

  const executeSearch = useCallback(
    (clearFilters?: boolean, clearSearch?: boolean) => {
      const queryFilters = {
        ...filters,
        neighborhoodId: neighborhoodId,
        vendorId: vendorId || undefined,
        startDate: selectedDateRange?.startDate?.toISOString() || undefined,
        endDate: selectedDateRange?.endDate?.toISOString() || undefined,
        lotNumber: selectedLotNumber || undefined,
        assignedTo: selectedAssigneedTo || undefined,
        searchedValue: searchValue
      };
      if (clearSearch) {
        updateQueryString({ ...queryFilters, searchedValue: '' });
        setSearchedValue('');
        setSearchValue('');
      }
      if (clearFilters) {
        setFilters({
          neighborhoodId: '',
          lotNumber: '',
          vendorId: '',
          startDate: undefined,
          endDate: undefined,
          itemType: undefined,
          assignedTo: undefined
        });
        setNewFilters({});
      } else {
        setPage(0);
        setSearchedValue(searchValue);
        setFilters(queryFilters);

        setNewFilters(queryFilters);
      }
    },
    [
      filters,
      neighborhoodId,
      selectedDateRange,
      selectedLotNumber,
      selectedAssigneedTo,
      searchValue,
      vendorId,
      updateQueryString
    ]
  );

  useEffect(() => {
    if (neighborhoodId && isNeighborhoodSearch) {
      setIsNeighborhoodSearch(false);
      setNewFilters({ ...filters, neighborhoodId: neighborhoodId });
      executeSearch();
    }
  }, [neighborhoodId, isNeighborhoodSearch, filters, executeSearch]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    updateQueryString(newFilters);
  }, [newFilters, updateQueryString]);

  // initialize the data
  const punchlists = punchlistData?.records ?? [];

  const columns = useMemo(() => {
    return [
      {
        Header: 'Punchlist Name',
        accessor: 'punchListName',
        sort: true,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPunchlistDashboard } };
        }) => {
          return (
            <div>
              <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                <Link to={`/production/punchlists/${original.punchListId}?${queryString}`}>
                  {original.punchListName}
                </Link>
              </Typography>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [punchlistData]);

  return (
    <>
      <Page title="Punchlist Dashboard">
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8} xl={9}>
            <PageTitleWithButton
              pageTitleProps={{
                marginBottom: false,
                title: 'Punchlist Dashboard',
                subTitle: 'Latest activity across your projects'
              }}
              button={
                <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                  <ButtonGroup>
                    <FormControlLabel
                      className="print--none"
                      sx={{ marginBottom: { xs: theme.spacing(0.5), sm: 'auto' } }}
                      control={
                        <Switch
                          color="primary"
                          checked={showArchived}
                          id="archived-active"
                          disabled={isLoadingPunchlists}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setShowArchived(e.target.checked);
                          }}
                          name={'archived-active'}
                        />
                      }
                      label={'Include Archived'}
                    />
                  </ButtonGroup>
                  <Box sx={{ flexBasis: { xs: 'auto', sm: '100%' }, height: 0 }}></Box>
                  <ButtonGroup
                    // reduce margin in button group slightly to prevent over sized mobile buttons in toolbar
                    sx={{
                      [theme.breakpoints.down('sm')]: {
                        '& .MuiButton-startIcon': {
                          marginRight: '4px'
                        }
                      }
                    }}
                  >
                    <Button
                      id="create-punchlist"
                      asLink
                      to="/production/punchlists/create-punchlist"
                      startIcon={<Add />}
                      color="primary"
                      variant="contained"
                      className="print--none"
                    >
                      Create Punchlist
                    </Button>
                    <Button
                      id="add-todo"
                      onClick={() => {
                        showTodoModal(true);
                      }}
                      variant="contained"
                      color="primary"
                      startIcon={<Add />}
                    >
                      Add Todo
                    </Button>
                    <Button
                      id={'csv_download'}
                      color="primary"
                      variant="contained"
                      disabled={isLoadingPunchlists}
                      // Hide this button on mobile
                      sx={{
                        display: {
                          xs: 'none',
                          md: 'none',
                          lg: 'inline-flex'
                        }
                      }}
                      onClick={async () => {
                        try {
                          const filtersForCsv = {
                            neighborhoodId: neighborhoodId || undefined,
                            vendorId: vendorId || undefined,
                            startDate: selectedDateRange?.startDate?.toISOString() || undefined,
                            endDate: selectedDateRange?.endDate?.toISOString() || undefined,
                            lotNumber: selectedLotNumber || undefined,
                            assignedTo: selectedAssigneedTo || undefined
                          };

                          let csv = await getPunchlistDashboardCsv(filtersForCsv);
                          const blob = new Blob([csv], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `allPunchlists_${format(
                            parseISO(new Date().toISOString()),
                            'yyyy_LL_dd_hh_mm_ss'
                          )}.csv`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        } catch (error) {
                          console.log(error);
                        }
                      }}
                      startIcon={<CloudDownload />}
                    >
                      CSV
                    </Button>
                    <LoadingButton
                      loading={isDownloadingPdf}
                      loadingPosition="start"
                      color="secondary"
                      variant="contained"
                      disabled={isDownloadingPdf || isLoadingPunchlists}
                      onClick={async () => {
                        try {
                          setIsDownloadingPdf(true);
                          const blob = await getPunchlistTodoListPdf({
                            includeArchived: showArchived,
                            neighborhoodId: neighborhoodId || undefined,
                            lotNumber: selectedLotNumber || undefined,
                            vendorId: vendorId || undefined,
                            startDate: selectedDateRange?.startDate?.toISOString() || undefined,
                            endDate: selectedDateRange?.endDate?.toISOString() || undefined
                          });

                          if (blob) {
                            downloadFile(blob, 'punchlist_todos.pdf');
                          } else {
                            throw new Error('No PDF data received');
                          }
                        } catch (error) {
                          enqueueSnackbar(error?.message ?? `Error downloading PDF file`, {
                            variant: 'error'
                          });
                        } finally {
                          setIsDownloadingPdf(false);
                        }
                      }}
                      startIcon={<CloudDownload />}
                    >
                      PDF
                    </LoadingButton>
                  </ButtonGroup>
                </Box>
              }
            />
            <div className="print--none">
              <PunchlistFilters
                isLoading={isLoadingPunchlists}
                selectedNeighborhoodId={neighborhoodId}
                selectedVendorId={vendorId}
                setSelectedVendorId={setVendorId}
                selectedDateRange={selectedDateRange}
                setSelectedDateRange={setSelectedDateRange}
                setSelectedNeighborhoodId={setNeighborhoodId}
                selectedLotNumber={selectedLotNumber}
                setSelectedLotNumber={setSelectedLotNumber}
                setHasAppliedFilters={setHasAppliedFilters}
                hasAppliedFilters={hasAppliedFilters}
                setSearchValue={setSearchValue}
                setSearchedValue={setSearchedValue}
                searchValue={searchValue}
                showAssignedToFilter={showAssignedToFilter}
                setShowAssignedToFilter={setShowAssignedToFilter}
                selectedAssigneedTo={selectedAssigneedTo}
                setSelectedAssigneedTo={setSelectedAssigneedTo}
                handleSearch={(val: string) => {
                  setPage(0);
                  setSearchedValue(val);

                  updateQueryString({ ...urlFilters, searchedValue: val });
                }}
                handleClearSearch={() => {
                  updateQueryString({ ...newFilters, searchedValue: '' });
                  setSearchValue('');
                  setSearchedValue('');
                  setHasAppliedFilters(false);
                  setPage(0);
                }}
                applyFilters={executeSearch}
              />
            </div>
            {searchedValue && (
              <SearchSection
                searchedValue={searchedValue}
                handleClearSearch={() => {
                  updateQueryString({ ...newFilters, searchedValue: '' });
                  setSearchValue('');
                  setSearchedValue('');
                  setHasAppliedFilters(false);
                  setPage(0);
                }}
              />
            )}
            {isLoadingPunchlists && <Loader position="top-center" type="inline" />}
            {!isLoadingPunchlists && punchlists?.length === 0 && (
              <Box display="flex" justifyContent="center" marginTop="1rem">
                <Typography variant="body1" gutterBottom>
                  No punchlists found.
                </Typography>
              </Box>
            )}
            {!isLoadingPunchlists && (
              <TableComponent
                columns={columns}
                data={punchlists}
                isLoading={isLoadingPunchlists}
                hidePagination
              />
            )}
            {!isLoadingPunchlists && punchlists?.length > 0 && (
              <Box
                sx={{
                  backgroundColor: theme => theme.palette.grey[200],
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Pagination
                  page={page}
                  count={recordCount}
                  rowsPerPage={perPage}
                  setPage={newPage => {
                    setNewFilters({ ...newFilters, page: newPage + 1 });
                    setPage(newPage);
                  }}
                  setRowsPerPage={rowsPerPage => {
                    setNewFilters({ ...newFilters, page: 1, perPage: rowsPerPage });
                    updateQueryString({ ...newFilters, page: 1 });
                    updateQueryString({ ...newFilters, perPage: rowsPerPage });
                    setRowsPerPage(rowsPerPage);
                    setPage(0);
                  }}
                  labelRowsPerPage="Punchlists per page:"
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} lg={4} xl={3} flexDirection="column" display="flex">
            <Box padding="1rem" border="1px solid #e1e1e1" borderRadius="4px" flex={1}>
              <NeighborhoodList
                setNeighborhoodId={setNeighborhoodId}
                setIsNeighborhoodSearch={setIsNeighborhoodSearch}
              />
            </Box>
          </Grid>
        </Grid>
        <TodoDetailModal
          open={isTodoModalOpen}
          handleClose={(shouldRefresh?: boolean, shouldClose = true) => {
            if (shouldRefresh) {
              refetchPunchlistsRecentActivity();
            }
            if (shouldClose) showTodoModal(false);
          }}
        />
      </Page>
      <div style={{ display: 'none' }}>
        <div>
          {punchlists.map((punchlist, i) => (
            <Fragment key={punchlist.punchListId}>
              <Typography variant="h6" style={{ margin: '10px' }}>
                {punchlist.punchListName}
              </Typography>
              <hr style={{ border: '1px solid black' }} />
            </Fragment>
          ))}
        </div>
      </div>
    </>
  );
};
